import { useEffect, useState } from "react";
import { MdClose, MdDelete, MdVisibility } from "react-icons/md";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { adminApiService } from "services/api";

Modal.setAppElement("#root");

export default function ReviewApprovalTable() {
  const [reviews, setReviews] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  const fetchReviews = async () => {
    try {
      const response = await adminApiService.getProductReview();
      if (!response.ok) throw new Error("Failed to fetch product reviews.");
      const data = await response.json();
      setReviews(data.reviews);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching product reviews.");
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleDeleteReview = async (reviewId) => {
    try {
      const response = await adminApiService.deleteProductReview(reviewId);
      if (!response.ok) throw new Error("Failed to delete review.");
      toast.success("Review deleted successfully!");
      fetchReviews();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error deleting review.");
    }
  };

  const handleToggleApproval = async (reviewId, currentStatus) => {
    const newStatus = currentStatus ? false : true;
    try {
      const response = await adminApiService.approveProductReview({
        approved: newStatus,
        id: reviewId,
      });

      if (!response.ok)
        throw new Error("Failed to update review approval status.");
      toast.success("Review status updated successfully!");
      fetchReviews();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Error updating review approval status.");
    }
  };

  const openModal = (review) => {
    setSelectedReview(review);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedReview(null);
  };

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <table className="w-full text-left text-sm text-gray-500">
        <thead className="bg-primary text-xs uppercase text-white">
          <tr>
            <th className="px-6 py-3">ID</th>
            <th className="px-6 py-3">Rating</th>
            <th className="px-6 py-3">Review</th>
            <th className="px-6 py-3">User ID</th>
            <th className="px-6 py-3">Product ID</th>
            <th className="px-6 py-3">Status</th>
            <th className="px-6 py-3">Action</th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((review, index) => (
            <tr key={index} className="border-b bg-white">
              <td className="px-6 py-4 text-base text-navy-900">{review.id}</td>
              <td className="px-6 py-4 text-base text-navy-900">
                {review.rating}
              </td>
              <td className="px-6 py-4 text-base text-navy-900">
                {review.review_text}
              </td>
              <td className="px-6 py-4 text-base text-navy-900">
                {review.user_id}
              </td>
              <td className="px-6 py-4 text-base text-navy-900">
                {review.product_id}
              </td>
              <td className="px-6 py-4">
                <label className="inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={review.approved === true}
                    onChange={() =>
                      handleToggleApproval(review.id, review.approved)
                    }
                  />
                  <div className="peer relative h-6 w-11 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-checked:bg-primary dark:peer-focus:ring-blue-800 rtl:peer-checked:after:-translate-x-full"></div>
                </label>
              </td>
              <td className="px-6 py-4 ">
                <button
                  aria-label="View Review"
                  className="p-2 text-2xl text-primary hover:bg-gray-100"
                  onClick={() => openModal(review)}
                >
                  <MdVisibility />
                </button>
                <button
                  aria-label="View Review"
                  className="p-2 text-2xl text-primary hover:bg-gray-100"
                  onClick={() => handleDeleteReview(review.id)}
                >
                  <MdDelete />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Review Details"
        className="text-black relative mx-auto max-w-screen-lg rounded-lg bg-white p-6 shadow-lg"
        overlayClassName="fixed inset-0 flex items-center justify-center bg-navy-900 bg-opacity-50"
      >
        <button onClick={closeModal} className="close-button">
          <MdClose />
        </button>
        {selectedReview && (
          <div className="review-details">
            <h2 className="mb-4 text-xl font-semibold">Review Details</h2>
            <p>
              <strong>ID:</strong> {selectedReview.id}
            </p>
            <p>
              <strong>Rating:</strong> {selectedReview.rating}
            </p>
            <p>
              <strong>Review:</strong> {selectedReview.review_text}
            </p>
            <p>
              <strong>User ID:</strong> {selectedReview.user_id}
            </p>
            <p>
              <strong>Product ID:</strong> {selectedReview.product_id}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              {selectedReview.approved ? "Approved" : "Pending"}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
